import { useCallback, useMemo } from 'react'
import { useOverlay } from '../../contexts'
import { IPageMeta, IVideo } from '../../types'
import {
  mediaCdnReplace,
  secondsFormatted,
  verticalPosterUrl,
} from '../../utils'
import { PlayIcon } from '../GridBlocks/Reels/icons'
import { ItemVerticalPlayer } from './ItemVerticalPlayer'
import { reelsHe } from '../../locales/reels.he'
import {
  Container,
  Duration,
  DurationIcon,
  PlayButton,
  Poster,
  Shade,
} from './VerticalArticleVideo.styles'

type Props = {
  pageMeta: IPageMeta
  video: IVideo
  /**
   * Set for a video inside the article body. Like the horizontal player, such
   * a video titles itself from its own description only, with no fallback to
   * the article title.
   */
  fromParser?: boolean
}

/**
 * RESH13-1138 - a vertical video inside an article is shown as a 9/16 poster;
 * clicking it opens the vertical player fullscreen. Used both for the main
 * video of an item and for a video in the body.
 */
export const VerticalArticleVideo = ({
  pageMeta,
  video,
  fromParser = false,
}: Props) => {
  const { setIsOpen, setBody } = useOverlay()

  const title = useMemo(
    () => video.description || (fromParser ? '' : pageMeta.title),
    [fromParser, pageMeta.title, video.description]
  )
  const poster = useMemo(
    () =>
      video.poster ? verticalPosterUrl(mediaCdnReplace(video.poster)) : '',
    [video.poster]
  )

  const openPlayer = useCallback(() => {
    setBody(
      <ItemVerticalPlayer
        video={video}
        pageMeta={pageMeta}
        title={title}
        poster={poster}
      />
    )
    setIsOpen(true)
  }, [pageMeta, poster, setBody, setIsOpen, title, video])

  return (
    <Container
      role='button'
      tabIndex={0}
      aria-label={title || reelsHe.play}
      onClick={openPlayer}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openPlayer()
        }
      }}
    >
      {!!poster && <Poster src={poster} alt={title} loading='lazy' />}
      <Shade />
      <PlayButton>
        <PlayIcon />
      </PlayButton>
      {/* the closed state carries no caption over the poster, only the length */}
      {video.duration > 0 && (
        <Duration>
          <DurationIcon>
            <PlayIcon />
          </DurationIcon>
          <p>{secondsFormatted(video.duration)}</p>
        </Duration>
      )}
    </Container>
  )
}
